import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Visibility, VisibilityOff, Person, Lock } from '@mui/icons-material';
import { authApi } from '../../api/authApi';
import { login, clearError } from '../../store/slices/authSlice';
import { AppDispatch, RootState } from '../../store/store';
import { getDefaultAuthorizedRoute } from '../../utils/permissionAccess';
import LanguagePreferenceSelect from '../LanguagePreferenceSelect';
import { useLanguage } from '../../i18n/LocalizationContext';

const Login: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const { loading, error, isAuthenticated, user } = useSelector((state: RootState) => state.auth);

  const RESEND_OTP_COOLDOWN_SECONDS = 60;

  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [forgotPasswordStep, setForgotPasswordStep] = useState<'request' | 'reset'>('request');
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordMobile, setForgotPasswordMobile] = useState('');
  const [forgotPasswordOtp, setForgotPasswordOtp] = useState('');
  const [forgotPasswordNewPassword, setForgotPasswordNewPassword] = useState('');
  const [forgotPasswordConfirmPassword, setForgotPasswordConfirmPassword] = useState('');
  const [forgotPasswordOtpId, setForgotPasswordOtpId] = useState('');
  const [forgotPasswordSendingOtp, setForgotPasswordSendingOtp] = useState(false);
  const [forgotPasswordResendingOtp, setForgotPasswordResendingOtp] = useState(false);
  const [forgotPasswordResetting, setForgotPasswordResetting] = useState(false);
  const [forgotPasswordError, setForgotPasswordError] = useState<string | null>(null);
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState<string | null>(null);
  const [resetMessage, setResetMessage] = useState<string | null>(null);
  // Landing here after an idle sign-out looks like a random logout unless we say what happened.
  const [showTimeoutNotice, setShowTimeoutNotice] = useState(
    new URLSearchParams(location.search).get('reason') === 'timeout'
  );
  const [showForgotPasswordValue, setShowForgotPasswordValue] = useState(false);
  const [mobileError, setMobileError] = useState(false);
  const [resendOtpSecondsRemaining, setResendOtpSecondsRemaining] = useState(0);
  const isValidMobile = (num: string) => /^[0-9]{10}$/.test(num);

  const forgotPasswordBusy = forgotPasswordSendingOtp || forgotPasswordResendingOtp || forgotPasswordResetting;
  const forgotPasswordEmailTrimmed = forgotPasswordEmail.trim();
  const forgotPasswordMobileTrimmed = forgotPasswordMobile.trim();
  const isForgotPasswordMobileValid = isValidMobile(forgotPasswordMobileTrimmed);

  useEffect(() => {
    if (resendOtpSecondsRemaining <= 0) return;

    const intervalId = window.setInterval(() => {
      setResendOtpSecondsRemaining((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [resendOtpSecondsRemaining > 0]);

  useEffect(() => {
    if (isAuthenticated && user) {
      navigate(getDefaultAuthorizedRoute(user), { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(login({ identifier, password }));
  };

  const resetForgotPasswordState = () => {
    setForgotPasswordStep('request');
    setForgotPasswordEmail('');
    setForgotPasswordMobile('');
    setForgotPasswordOtp('');
    setForgotPasswordNewPassword('');
    setForgotPasswordConfirmPassword('');
    setForgotPasswordOtpId('');
    setForgotPasswordSendingOtp(false);
    setForgotPasswordResendingOtp(false);
    setForgotPasswordResetting(false);
    setForgotPasswordError(null);
    setShowForgotPasswordValue(false);
    setMobileError(false);
    setResendOtpSecondsRemaining(0);
  };

  const handleOpenForgotPassword = () => {
    setForgotPasswordSuccess(null);
    setForgotPasswordError(null);
    setForgotPasswordOpen(true);
    setResetMessage(null);
  };

  const handleCloseForgotPassword = () => {
    setForgotPasswordOpen(false);
    resetForgotPasswordState();
    setResetMessage(null);
  };

  const startResendOtpCooldown = () => {
    setResendOtpSecondsRemaining(RESEND_OTP_COOLDOWN_SECONDS);
  };

  const handleSendOtp = async () => {
    if (!forgotPasswordEmailTrimmed || !forgotPasswordMobileTrimmed) return;

    if (!isForgotPasswordMobileValid) {
      setMobileError(true);
      return;
    }

    setForgotPasswordSendingOtp(true);
    setForgotPasswordError(null);

    try {
      const response = await authApi.sendForgotPasswordOtp({
        email: forgotPasswordEmailTrimmed,
        mobileNumber: forgotPasswordMobileTrimmed,
      });

      setForgotPasswordOtpId(response.otpId);
      setForgotPasswordStep('reset');
      setForgotPasswordOtp('');
      setForgotPasswordNewPassword('');
      setForgotPasswordConfirmPassword('');
      setForgotPasswordSuccess(response.message);
      setResetMessage(response.message);
      startResendOtpCooldown();
    } catch (apiError: any) {
      setForgotPasswordError(t('messages.otpFailed'));
    } finally {
      setForgotPasswordSendingOtp(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendOtpSecondsRemaining > 0) return;
    if (!forgotPasswordEmailTrimmed || !forgotPasswordMobileTrimmed) return;
    if (!isForgotPasswordMobileValid) return;

    setForgotPasswordResendingOtp(true);
    setForgotPasswordError(null);

    try {
      const response = await authApi.sendForgotPasswordOtp({
        email: forgotPasswordEmailTrimmed,
        mobileNumber: forgotPasswordMobileTrimmed,
      });

      setForgotPasswordOtpId(response.otpId);
      setForgotPasswordOtp('');
      startResendOtpCooldown();
    } catch (apiError: any) {
      setForgotPasswordError(t('messages.otpFailed'));
    } finally {
      setForgotPasswordResendingOtp(false);
    }
  };

  const handleEditForgotPasswordDetails = () => {
    setForgotPasswordError(null);
    setForgotPasswordSuccess(null);
    setResetMessage(null);
    setForgotPasswordStep('request');
    setForgotPasswordOtp('');
    setForgotPasswordNewPassword('');
    setForgotPasswordConfirmPassword('');
    setForgotPasswordOtpId('');
    setResendOtpSecondsRemaining(0);
    setMobileError(forgotPasswordMobileTrimmed.length > 0 && !isForgotPasswordMobileValid);
  };

  const handleResetPassword = async () => {
    if (forgotPasswordNewPassword !== forgotPasswordConfirmPassword) {
      setForgotPasswordError(t('validation.passwordsDoNotMatch'));
      return;
    }

    setForgotPasswordResetting(true);
    setForgotPasswordError(null);

    try {
      const verifyResponse = await authApi.verifyForgotPasswordOtp({
        otpId: forgotPasswordOtpId,
        email: forgotPasswordEmailTrimmed,
        mobileNumber: forgotPasswordMobileTrimmed,
        otp: forgotPasswordOtp.trim(),
      });

      const resetResponse = await authApi.resetForgotPassword({
        otpId: forgotPasswordOtpId,
        email: forgotPasswordEmailTrimmed,
        mobileNumber: forgotPasswordMobileTrimmed,
        verificationToken: verifyResponse.verificationToken,
        newPassword: forgotPasswordNewPassword,
        confirmPassword: forgotPasswordConfirmPassword,
      });

      handleCloseForgotPassword();
      setForgotPasswordSuccess(resetResponse.message);
      setResetMessage(resetResponse.message);
    } catch (apiError: any) {
      setForgotPasswordError(apiError.response?.data?.message || t('messages.resetPasswordFailed'));
    } finally {
      setForgotPasswordResetting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      }}
    >
      <Paper
        elevation={8}
        sx={{
          p: 4,
          width: '100%',
          maxWidth: 420,
          borderRadius: 3,
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h4" fontWeight="bold" color="primary">
            {t('navigation.hrms')}
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={1}>
            {t('auth.signInTitle')}
          </Typography>
        </Box>

        {showTimeoutNotice && (
          <Alert severity="info" sx={{ mb: 2 }} onClose={() => setShowTimeoutNotice(false)}>
            {t('auth.sessionTimedOut')}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => dispatch(clearError())}>
            {t(error)}
          </Alert>
        )}

        {resetMessage && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setResetMessage(null)}>
            {resetMessage}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
         <TextField
              label={t('auth.emailOrMobile')}
              type="text"
              fullWidth
              required
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              onBlur={(e) => setIdentifier(e.target.value.trim())}
              margin="normal"
              inputProps={{ maxLength: 100 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person color="action" />
                  </InputAdornment>
                ),
              }}
            />
          <TextField
            label={t('auth.password')}
            type={showPassword ? 'text' : 'password'}
            fullWidth
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Link component="button" type="button" underline="hover" sx={{ fontSize: 14 }} onClick={handleOpenForgotPassword}>
              {t('auth.forgotPassword')}
            </Link>
          </Box>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading}
            sx={{ mt: 3, mb: 2, py: 1.5, borderRadius: 2 }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : t('auth.signIn')}
          </Button>

          <LanguagePreferenceSelect />

          {/* <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <Link component={RouterLink} to="/employee-registration" underline="hover" sx={{ fontSize: 14 }}>
              New employee registration
            </Link>
          </Box> */}
        </form>
      </Paper>
        <Dialog
          open={forgotPasswordOpen}
          onClose={(_, reason) => {
            if (reason === 'backdropClick') {
              return; // ❌ prevent closing on outside click
            }
            if (forgotPasswordBusy) {
              return; // optional: prevent close while API running
            }
            handleCloseForgotPassword();
          }}
          disableEscapeKeyDown={forgotPasswordBusy} // optional
          fullWidth
          maxWidth="xs"
        >
        <DialogTitle>{t('auth.forgotPasswordTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 0.5 }}>
            {t('auth.registeredContactHelp')}
          </Typography>

           {forgotPasswordSuccess && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setForgotPasswordSuccess(null)}>
            {forgotPasswordSuccess}
          </Alert>
        )}

          {forgotPasswordError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setForgotPasswordError(null)}>
              {forgotPasswordError}
            </Alert>
          )}

          {forgotPasswordStep === 'request' && (
            <>
              <TextField
                label={t('common.email')}
                type="email"
                fullWidth
                required
                value={forgotPasswordEmail}
                onChange={(e) => setForgotPasswordEmail(e.target.value)}
                onBlur={(e) => setForgotPasswordEmail(e.target.value.trim())}
                margin="normal"
              />
             <TextField
              label={t('auth.mobileNumber')}
              type="tel"
              fullWidth
              required
              value={forgotPasswordMobile}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, ""); // only digits
                setForgotPasswordMobile(value);
                setMobileError(false);
              }}
              onBlur={() => {
                const trimmed = forgotPasswordMobile.trim();
                setForgotPasswordMobile(trimmed);
                setMobileError(!isValidMobile(trimmed));
              }}
              error={mobileError}
              helperText={
                mobileError ? t('auth.validMobile') : ""
              }
              margin="normal"
              inputProps={{ maxLength: 10 }}
            />
            </>
          )}

          {forgotPasswordStep === 'reset' && (
            <>
              <TextField
                label={t('auth.otp')}
                type="text"
                fullWidth
                required
                value={forgotPasswordOtp}
                onChange={(e) => setForgotPasswordOtp(e.target.value)}
                onBlur={(e) => setForgotPasswordOtp(e.target.value.trim())}
                margin="normal"
                autoComplete="one-time-code"
                name="otp_field"
              />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                <Button
                  variant="text"
                  size="small"
                  onClick={handleEditForgotPasswordDetails}
                  disabled={forgotPasswordBusy}
                  sx={{ px: 0 }}
                >
                  {t('auth.editContact')}
                </Button>
                <Button
                  variant="text"
                  size="small"
                  onClick={handleResendOtp}
                  disabled={forgotPasswordResendingOtp || forgotPasswordResetting || resendOtpSecondsRemaining > 0}
                  sx={{ px: 0 }}
                >
                  {forgotPasswordResendingOtp ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : resendOtpSecondsRemaining > 0 ? (
                    t('auth.resendOtpIn', { seconds: resendOtpSecondsRemaining })
                  ) : (
                    t('auth.resendOtp')
                  )}
                </Button>
              </Box>
              <TextField
                label={t('auth.newPassword')}
                type={showForgotPasswordValue ? 'text' : 'password'}
                fullWidth
                required
                value={forgotPasswordNewPassword}
                onChange={(e) => setForgotPasswordNewPassword(e.target.value)}
                autoComplete="new-password"
                name="new_password"
                margin="normal"
                InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowForgotPasswordValue(!showForgotPasswordValue)} edge="end">
                          {showForgotPasswordValue ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                }}
              />
              {/* <TextField
                label="Confirm Password"
                type={showForgotPasswordValue ? 'text' : 'password'}
                fullWidth
                required
                value={forgotPasswordConfirmPassword}
                onChange={(e) => setForgotPasswordConfirmPassword(e.target.value)}
                margin="normal"
              /> */}
              <TextField
                label={t('auth.confirmPassword')}
                type={showForgotPasswordValue ? 'text' : 'password'}
                fullWidth
                required
                value={forgotPasswordConfirmPassword}
                onChange={(e) => setForgotPasswordConfirmPassword(e.target.value)}
                margin="normal"
                autoComplete="new-password"
                name="confirm_password"
              />

            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseForgotPassword} disabled={forgotPasswordBusy}>
            {t('common.cancel')}
          </Button>
          {forgotPasswordStep === 'request' ? (
            <Button
              onClick={handleSendOtp}
              variant="contained"
              disabled={forgotPasswordSendingOtp || !forgotPasswordEmailTrimmed || !forgotPasswordMobileTrimmed}
            >
              {forgotPasswordSendingOtp ? <CircularProgress size={20} color="inherit" /> : t('auth.sendOtp')}
            </Button>
          ) : (
            <Button
              onClick={handleResetPassword}
              variant="contained"
              disabled={
                forgotPasswordResetting
                || !forgotPasswordOtp.trim()
                || !forgotPasswordNewPassword
                || !forgotPasswordConfirmPassword
              }
            >
              {forgotPasswordResetting ? <CircularProgress size={20} color="inherit" /> : t('auth.resetPassword')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Login;
